/**
 * History range math for the client cache (SPEC §6.8, SPEC-ADDENDA A1, ADR-0004). Pure functions:
 * no IndexedDB, no sockets, no protobuf — everything here is unit-tested under node.
 *
 * - Times are integer epoch **milliseconds**; every range is half-open `[from, to)`.
 * - The cache is segmented by **UTC day** (`dayStart`), keyed `(user, contract, barSpec, day)`
 *   (§6.8). UTC days have no DST, and every supported time-bar period divides a day, so a bar never
 *   straddles two segments.
 * - A cached day records `coveredTo`: the bars for `[dayStart, coveredTo)` are known complete.
 *   Prior days are immutable once `coveredTo` reaches the day's end (A1.3); the current day is
 *   extended from `coveredTo` to the live edge on the next open, never refetched from scratch.
 * - Prices stay integer ticks end to end; bars are packed as float64 only because every field is an
 *   integer below 2^53.
 */
import {
  BarBuilder,
  inTimeOrder,
  mergeDeltaExtremes,
  sessionKey,
  sessionOpenMs,
  type BarRecord,
  type BarSpec,
} from "./barBuilder.ts";
import { TradingDayCache, tradingDayCloseMs, tradingDayOf, tradingDayOpenMs, weekday } from "./calendar.ts";

export const DAY_MS = 86_400_000;
/** A1.1 default initial load for minute/hour bars: 10 trading days. */
export const INITIAL_TRADING_DAYS = 10;
/** Initial load for daily bars (no spec default; ~1 year). */
export const INITIAL_DAILY_DAYS = 365;
/** Weekly bars are built from daily history: ~3 years of days. */
export const INITIAL_WEEKLY_DAYS = 3 * 365;
/** Monthly bars (any grouping) are built from daily history: ~6 years of days. */
export const INITIAL_MONTHLY_DAYS = 6 * 365;
/** Trade-built bars: the current plus the prior session (A1.1). */
export const INITIAL_TRADE_SESSIONS = 2;

export type HistRange = { from: number; to: number };

/**
 * Stable key for a bar spec: the cache key part of fetch specs ("t60000" time ms, "n1" raw
 * trades) and the identity of display specs ("D1", "W1", "M3", "v1000", "r8", "k4").
 */
export function specKey(spec: BarSpec): string {
  switch (spec.kind) {
    case "time":
      return `t${String(spec.periodMs)}`;
    case "session":
      return `${spec.unit === "day" ? "D" : spec.unit === "week" ? "W" : "M"}${String(spec.count)}`;
    case "tick":
      return `n${String(spec.count)}`;
    case "volume":
      return `v${String(spec.count)}`;
    case "range":
      // "n": phantom bars off (Q-T5); the default keeps the old key.
      return `r${String(spec.ticks)}${spec.phantom === false ? "n" : ""}`;
    case "renko":
      return `k${String(spec.ticks)}`;
  }
}

/**
 * A cache spec key as the Settings → Data page shows it: "t60000" → "1m bars", "t86400000" →
 * "daily bars", "n1" → "raw trades". Only fetch specs are cached (`fetchSpecOf`).
 */
export function cacheSpecLabel(key: string): string {
  if (key === "n1") return "raw trades";
  const m = /^t(\d+)$/.exec(key);
  if (!m) return key;
  const ms = Number(m[1]);
  if (ms === DAY_MS) return "daily bars";
  if (ms % 60_000 === 0) return `${String(ms / 60_000)}m bars`;
  if (ms % 1000 === 0) return `${String(ms / 1000)}s bars`;
  return key;
}

/** `helm.v1.BarSpec.Kind` values (kept numeric so this module never imports generated code). */
export const PROTO_KIND = { SECOND: 1, MINUTE: 2, DAILY: 3, TICK: 5, RAW_TRADES: 8 } as const;

/** Raw trades (one-tick replay): the fetch spec of every trade-built bar type. */
export const RAW_TRADES: BarSpec = { kind: "tick", count: 1 };
/**
 * Rithmic daily bars as the gateway serves them: one bar per trade date, `openTs` = 00:00 UTC of
 * that date (§6.8 "indices are YYYYMMDD"). The fetch spec of 1D / 1W / Nmo charts, cached per UTC
 * day under the key the old UTC-day "1D" used (same data, same packed layout).
 */
export const DAILY_WIRE: BarSpec = { kind: "time", periodMs: DAY_MS };
/** 1-minute bars: the fetch spec of every minute/hour chart and of the session tail. */
export const MINUTE_BARS: BarSpec = { kind: "time", periodMs: 60_000 };
const SECOND_BARS: BarSpec = { kind: "time", periodMs: 1000 };

/**
 * What history is fetched (and cached) as, for a chart's display spec:
 * - seconds that divide a minute (1s, 5s, 15s, 30s …) → themselves;
 * - every minute/hour period → 1m bars aggregated client-side: the forming bar is complete up to
 *   the last closed minute (history ends at the 1m live edge, live trades do the rest), the
 *   alignment is always the session-open one of `sessionBucket` (Q-T3) whatever Rithmic does for
 *   odd periods, and all minute timeframes share one 1m cache;
 * - other second periods (7s, 90s …) → 1s bars aggregated client-side;
 * - ND / NW / Nmo → Rithmic daily bars (`DAILY_WIRE`), days, weeks and months aggregated
 *   client-side, plus the current trading day from 1m bars (`HistoryLoader`'s session tail);
 * - tick / volume / range / Renko → raw trades (`RAW_TRADES`).
 */
export function fetchSpecOf(spec: BarSpec): BarSpec {
  switch (spec.kind) {
    case "time": {
      const p = spec.periodMs;
      if (p < 60_000 && 60_000 % p === 0) return spec;
      if (p % 60_000 !== 0) return SECOND_BARS;
      return p === 60_000 ? spec : MINUTE_BARS;
    }
    case "session":
      return DAILY_WIRE;
    default:
      return RAW_TRADES;
  }
}

/**
 * Fetched bars (time order) → the chart's bars. Identity when the chart shows the fetch spec;
 * otherwise aggregated into the display buckets (the last bucket may be partial: live trades
 * newer than the cutoff complete it). Daily wire bars move to their trading day's 17:00 CT open.
 */
export function toDisplayBars(display: BarSpec, fetched: readonly BarRecord[]): readonly BarRecord[] {
  if (display.kind === "time") {
    const p = display.periodMs;
    if (fetchSpecOf(display) === display) return fetched;
    // The fetched 1s/1m bars never straddle a display bucket: the session open is a whole minute.
    const days = new TradingDayCache();
    return aggregate(
      fetched,
      (ts) => days.bucketOpen(ts, p), // sessionBucket's open
      (k) => k,
    );
  }
  if (display.kind !== "session") return fetched;
  // Daily wire bars are dated by trade date (00:00 UTC of it).
  return aggregate(
    fetched,
    (ts) => sessionOpenOfDay(display, Math.floor(ts / DAY_MS)),
    (k) => k,
  );
}

/** Open (UTC ms) of the session bar containing trading day `day` (day id of its close date). */
export function sessionOpenOfDay(
  display: { unit: "day" | "week" | "month"; count: number },
  day: number,
): number {
  return sessionOpenMs(display.unit, display.count, sessionKey(display.unit, display.count, day));
}

/**
 * Drop leading display bars that the loaded history cannot complete: an aggregated bar (4h, 7m,
 * 2D, 1W …) whose bucket opens before the first loaded moment would show only its tail. `fromMs`
 * is the start of the loaded fetch range (fetch space). Identity display specs are unchanged.
 */
export function trimPartialHead(
  display: BarSpec,
  fromMs: number,
  bars: readonly BarRecord[],
): readonly BarRecord[] {
  if (!Number.isFinite(fromMs) || fetchSpecOf(display) === display) return bars;
  let first = fromMs;
  if (display.kind === "session") {
    // Daily wire bars are dated 00:00 UTC of their trade date: the first loaded trading day opens
    // at 17:00 CT the day before (a weekend date rolls forward to Monday's Sunday open).
    let d = Math.floor(fromMs / DAY_MS);
    while (weekday(d) === 0 || weekday(d) === 6) d++;
    first = tradingDayOpenMs(d);
  } else if (display.kind !== "time") return bars;
  let i = 0;
  while (i < bars.length && (bars[i]?.openTs ?? 0) < first) i++;
  return i === 0 ? bars : bars.slice(i);
}

/**
 * Session bars (1D/1W/Nmo) from daily history plus the current trading day's intraday bars (the
 * session tail, 1m): the tail is folded into the last daily-derived bar when it belongs to the same
 * week or month, otherwise appended.
 */
export function withSessionTail(
  display: BarSpec,
  daily: readonly BarRecord[],
  intraday: readonly BarRecord[],
): readonly BarRecord[] {
  if (display.kind !== "session" || intraday.length === 0) return daily;
  const tail = aggregate(
    intraday,
    (ts) => sessionOpenOfDay(display, tradingDayOf(ts)),
    (k) => k,
  );
  const out = daily.filter((b) => b.openTs <= (tail[0]?.openTs ?? Infinity));
  for (const t of tail) {
    const last = out[out.length - 1];
    if (last && last.openTs === t.openTs) {
      out[out.length - 1] = {
        ...last,
        ...mergeDeltaExtremes(last, t),
        high: Math.max(last.high, t.high),
        low: Math.min(last.low, t.low),
        close: t.close,
        volume: last.volume + t.volume,
        buyVolume: last.buyVolume + t.buyVolume,
        sellVolume: last.sellVolume + t.sellVolume,
        trades: last.trades + t.trades,
      };
    } else out.push(t);
  }
  return out;
}

/**
 * A fetch-space cutoff (history holds every trade before it) → display space. Daily wire: covered
 * through trade date d−1 means every trade before trading day d's 17:00 CT open.
 */
export function toDisplayCutoff(display: BarSpec, cutoff: number): number {
  if (display.kind !== "session" || !Number.isFinite(cutoff)) return cutoff;
  return tradingDayOpenMs(Math.floor(cutoff / DAY_MS));
}

function aggregate(
  bars: readonly BarRecord[],
  keyOf: (openTs: number) => number,
  openOf: (key: number) => number,
): BarRecord[] {
  const out: BarRecord[] = [];
  let key = Number.NaN;
  for (const b of bars) {
    const k = keyOf(b.openTs);
    const cur = out[out.length - 1];
    if (cur && k === key) {
      Object.assign(cur, mergeDeltaExtremes(cur, b));
      cur.high = Math.max(cur.high, b.high);
      cur.low = Math.min(cur.low, b.low);
      cur.close = b.close;
      cur.volume += b.volume;
      cur.buyVolume += b.buyVolume;
      cur.sellVolume += b.sellVolume;
      cur.trades += b.trades;
    } else {
      key = k;
      out.push({ ...b, openTs: openOf(k) });
    }
  }
  return out;
}

/** Map a fetch spec to the wire `BarSpec` (§6.8 table); null when the gateway cannot serve it. */
export function protoSpec(spec: BarSpec): { kind: number; period: number } | null {
  if (spec.kind === "tick")
    return spec.count === 1
      ? { kind: PROTO_KIND.RAW_TRADES, period: 1 }
      : { kind: PROTO_KIND.TICK, period: spec.count };
  if (spec.kind !== "time") return null;
  const p = spec.periodMs;
  if (p === DAY_MS) return { kind: PROTO_KIND.DAILY, period: 1 };
  if (p % 60_000 === 0 && p < DAY_MS) return { kind: PROTO_KIND.MINUTE, period: p / 60_000 };
  if (p % 1000 === 0 && p < 60_000) return { kind: PROTO_KIND.SECOND, period: p / 1000 };
  return null;
}

/**
 * Gateway replay-window model (crates/gateway/src/history.rs `plan_windows`): a request is split
 * into windows expected to hold ≤ 8 000 bars, and a request spanning more than 256 windows is
 * refused with `HISTORY_RANGE_TOO_LARGE`. The client splits its ranges so it never asks for more.
 */
export const GATEWAY_MAX_WINDOWS_PER_REQUEST = 256;
export const GATEWAY_BARS_PER_WINDOW = 8_000;

/** Wall-clock span (ms) of one gateway replay window for `spec`. */
export function gatewayWindowMs(spec: BarSpec): number {
  // Tick-type bars have no duration: 30 min × N, at most a day.
  if (spec.kind === "tick") return Math.min(1_800_000 * Math.max(1, spec.count), DAY_MS);
  if (spec.kind !== "time") return gatewayWindowMs(fetchSpecOf(spec));
  if (spec.periodMs >= DAY_MS) return GATEWAY_BARS_PER_WINDOW * DAY_MS;
  return Math.max(1000, spec.periodMs) * GATEWAY_BARS_PER_WINDOW;
}

/**
 * Longest range one `HistoryRequest` may span. One window is kept as slack: the gateway aligns
 * `from` down to a bar boundary and rounds `to` up to a whole second, which can add a window.
 */
export function maxRequestSpanMs(spec: BarSpec): number {
  return gatewayWindowMs(spec) * (GATEWAY_MAX_WINDOWS_PER_REQUEST - 1);
}

/** Split `r` into contiguous pieces the gateway accepts (oldest first). */
export function splitForGateway(r: HistRange, spec: BarSpec, maxSpan = maxRequestSpanMs(spec)): HistRange[] {
  if (!(r.to > r.from)) return [];
  const span = Math.max(1, maxSpan);
  const out: HistRange[] = [];
  for (let a = r.from; a < r.to; a += span) out.push({ from: a, to: Math.min(r.to, a + span) });
  return out;
}

/**
 * Only time bars are cached as bars. Trade-built bars depend on where counting started, so their
 * bars are never stored — the raw trades of each session are (`HistoryCache.writeSession`,
 * `TradeHistory`), and every tick/volume/range/Renko size is rebuilt from them.
 */
export function cacheable(spec: BarSpec): boolean {
  return spec.kind === "time";
}

export function dayStart(ms: number): number {
  return Math.floor(ms / DAY_MS) * DAY_MS;
}

/**
 * The exclusive end of what history should cover, for a fetch spec: the open of the bar that is
 * still forming (time bars — live trades build that one), 00:00 UTC of the current trading day's
 * date for daily wire bars (every earlier trade date is complete), or `now` for raw trades.
 */
export function liveEdge(spec: BarSpec, nowMs: number): number {
  if (spec.kind !== "time") return nowMs;
  if (spec.periodMs === DAY_MS) return tradingDayOf(nowMs) * DAY_MS;
  return Math.floor(nowMs / spec.periodMs) * spec.periodMs;
}

/** True for Monday–Friday (UTC). */
function isWeekday(dayStartMs: number): boolean {
  const wd = new Date(dayStartMs).getUTCDay();
  return wd !== 0 && wd !== 6;
}

/**
 * Default initial window (A1.1) for a display spec, in its fetch spec's space (`fetchSpecOf`):
 * seconds → the current UTC day; minutes/hours → the last 10 weekdays (weekend days in between
 * are included — they are empty and cost nothing extra since contiguous days go out as one
 * request); daily → ~1 year of trade dates, weekly ~3 years, monthly ~6 years; trade-built bars →
 * the current + prior session (`tradeSessions`; this returns their span). `from` is day-aligned
 * for time bars so the cache's day segments line up.
 */
export function initialRange(spec: BarSpec, nowMs: number): HistRange {
  const fetch = fetchSpecOf(spec);
  const to = liveEdge(fetch, nowMs);
  if (spec.kind === "session") {
    // N-day / N-week bars scale the 1D / 1W window (about the same number of bars), up to the
    // monthly window.
    const days =
      spec.unit === "day"
        ? Math.min(INITIAL_DAILY_DAYS * spec.count, INITIAL_MONTHLY_DAYS)
        : spec.unit === "week"
          ? Math.min(INITIAL_WEEKLY_DAYS * spec.count, INITIAL_MONTHLY_DAYS)
          : INITIAL_MONTHLY_DAYS;
    return { from: to - days * DAY_MS, to };
  }
  if (spec.kind !== "time") {
    const s = tradeSessions(nowMs, INITIAL_TRADE_SESSIONS);
    return { from: s[s.length - 1]?.from ?? nowMs, to: nowMs };
  }
  const today = dayStart(nowMs);
  // Seconds, and anything built from 1s bars (90s …): the current day only.
  if (fetch.kind === "time" && fetch.periodMs < 60_000) return { from: today, to };
  let d = today;
  let weekdays = isWeekday(d) ? 1 : 0;
  while (weekdays < INITIAL_TRADING_DAYS) {
    d -= DAY_MS;
    if (isWeekday(d)) weekdays++;
  }
  return { from: d, to };
}

/**
 * The last `count` trading sessions at `nowMs`, newest first, each `[17:00 CT open, min(16:00 CT
 * close, now))`. Before Sunday's open (weekend) the newest is Friday's complete session. Holidays
 * are not modelled: a holiday is a session whose replay returns no trades.
 */
export function tradeSessions(nowMs: number, count: number): HistRange[] {
  let d = tradingDayOf(nowMs);
  if (tradingDayOpenMs(d) > nowMs) d = previousTradingDay(d);
  const out: HistRange[] = [];
  for (let i = 0; i < count; i++) {
    out.push({ from: tradingDayOpenMs(d), to: Math.min(tradingDayCloseMs(d), nowMs) });
    d = previousTradingDay(d);
  }
  return out;
}

function previousTradingDay(d: number): number {
  let p = d - 1;
  while (weekday(p) === 0 || weekday(p) === 6) p--;
  return p;
}

/** Gateway one-tick replay window (crates/gateway/src/history.rs `plan_windows`: 30 min × 1). */
export const RAW_WINDOW_MS = 1_800_000;
/**
 * Replay windows per raw-trade request. The client scheduler allows 9 requests/min, so 2 windows
 * keep one chart at ≤ 20 replays/min — the gateway's per-session bucket (A1.2) — and requests go
 * out one at a time (A1.1 "tick replay warm-up … one window in flight").
 */
export const RAW_WINDOWS_PER_REQUEST = 2;

/** Split a session range into raw-trade requests of `RAW_WINDOWS_PER_REQUEST` windows (oldest first). */
export function rawPieces(r: HistRange): HistRange[] {
  return splitForGateway(r, RAW_TRADES, RAW_WINDOW_MS * RAW_WINDOWS_PER_REQUEST);
}

/** Day starts of every UTC day overlapping `[from, to)`. */
export function daysIn(r: HistRange): number[] {
  const out: number[] = [];
  if (!(r.to > r.from)) return out;
  for (let d = dayStart(r.from); d < r.to; d += DAY_MS) out.push(d);
  return out;
}

/** Merge touching/overlapping ranges; input in any order. */
export function coalesce(ranges: readonly HistRange[]): HistRange[] {
  const sorted = ranges.filter((r) => r.to > r.from).sort((a, b) => a.from - b.from);
  const out: HistRange[] = [];
  for (const r of sorted) {
    const last = out[out.length - 1];
    if (last && r.from <= last.to) last.to = Math.max(last.to, r.to);
    else out.push({ from: r.from, to: r.to });
  }
  return out;
}

/**
 * What must be fetched to cover `want`, given each cached day's `coveredTo` (a prefix of the day).
 * Contiguous gaps are coalesced so one request covers them (the gateway splits it into replay
 * windows and budgets each one).
 */
export function missingRanges(want: HistRange, covered: ReadonlyMap<number, number>): HistRange[] {
  const out: HistRange[] = [];
  for (const d of daysIn(want)) {
    const lo = Math.max(want.from, d);
    const hi = Math.min(want.to, d + DAY_MS);
    const c = covered.get(d);
    const start = c === undefined ? lo : Math.max(lo, Math.min(c, d + DAY_MS));
    if (start < hi) out.push({ from: start, to: hi });
  }
  return coalesce(out);
}

/**
 * The covered prefix of day `d` after adding `intervals` to what was covered before (`prevTo`):
 * the end of the contiguous run that starts at the day's start.
 */
export function coveredPrefix(d: number, prevTo: number, intervals: readonly HistRange[]): number {
  const end = d + DAY_MS;
  let to = Math.min(Math.max(prevTo, d), end);
  for (const r of coalesce(intervals)) {
    if (r.from > to) break;
    to = Math.max(to, Math.min(r.to, end));
  }
  return to;
}

/**
 * Merge two bar lists (each in time order) into one strictly increasing by `openTs`. On equal
 * open times `incoming` wins (a refetch replaces what was cached).
 */
export function mergeBars(base: readonly BarRecord[], incoming: readonly BarRecord[]): BarRecord[] {
  const a = inTimeOrder(base);
  const b = inTimeOrder(incoming);
  const out: BarRecord[] = [];
  let i = 0;
  let j = 0;
  while (i < a.length || j < b.length) {
    const x = a[i];
    const y = b[j];
    if (y === undefined || (x !== undefined && x.openTs < y.openTs)) {
      if (x) out.push(x);
      i++;
    } else if (x === undefined || y.openTs < x.openTs) {
      out.push(y);
      j++;
    } else {
      out.push(y);
      i++;
      j++;
    }
  }
  return out;
}

/**
 * A1.5 integrity check. Returns the first problem found, or null when the bars are usable:
 * non-integer fields, `high < low`, open/close outside the range, negative volume, or open times
 * that do not strictly increase.
 */
export function validateBars(bars: readonly BarRecord[]): string | null {
  let prev = -Infinity;
  for (const b of bars) {
    const ints = [b.openTs, b.open, b.high, b.low, b.close, b.volume, b.buyVolume, b.sellVolume, b.trades];
    if (!ints.every(Number.isSafeInteger)) return `non-integer field at ${String(b.openTs)}`;
    if (b.high < b.low) return `high < low at ${String(b.openTs)}`;
    if (b.open > b.high || b.open < b.low || b.close > b.high || b.close < b.low)
      return `open/close outside high-low at ${String(b.openTs)}`;
    if (b.volume < 0 || b.buyVolume < 0 || b.sellVolume < 0 || b.trades < 0)
      return `negative volume at ${String(b.openTs)}`;
    if (!(b.openTs > prev)) return `non-monotonic open time at ${String(b.openTs)}`;
    prev = b.openTs;
  }
  return null;
}

/**
 * Why a cached window is `suspect` (A1.5). The first three are integrity failures of the stored
 * data itself; `seam` means the history disagrees with bars the chart built from live trades.
 */
export type SuspectReason = "checksum" | "high-low" | "order" | "invalid" | "seam";

export const SUSPECT_TEXT: Record<SuspectReason, string> = {
  checksum: "bar-count checksum mismatch",
  "high-low": "a bar with high < low",
  order: "non-monotonic timestamps",
  invalid: "an invalid bar",
  seam: "seam mismatch with live data",
};

/** `validateBars` as a `SuspectReason` (null = usable). */
export function barProblem(bars: readonly BarRecord[]): SuspectReason | null {
  let prev = -Infinity;
  for (const b of bars) {
    const ints = [b.openTs, b.open, b.high, b.low, b.close, b.volume, b.buyVolume, b.sellVolume, b.trades];
    if (!ints.every(Number.isSafeInteger)) return "invalid";
    if (b.high < b.low) return "high-low";
    if (!(b.openTs > prev)) return "order";
    if (b.open > b.high || b.open < b.low || b.close > b.high || b.close < b.low) return "invalid";
    if (b.volume < 0 || b.buyVolume < 0 || b.sellVolume < 0 || b.trades < 0) return "invalid";
    prev = b.openTs;
  }
  return null;
}

/**
 * A marked stretch of the chart's time axis (display space, `[from, to)`): `suspect` history
 * (A1.5) or a `gap` the history plant could not fill (§6.8). Drawn as a hatched band.
 */
export type HistoryBand = { from: number; to: number; kind: "suspect" | "gap"; reason: string };

/** Merge bands of the same kind and reason that touch or overlap (time order out). */
export function mergeBands(bands: readonly HistoryBand[]): HistoryBand[] {
  const sorted = [...bands]
    .filter((b) => b.to > b.from)
    .sort((a, b) => a.from - b.from || a.kind.localeCompare(b.kind) || a.reason.localeCompare(b.reason));
  const out: HistoryBand[] = [];
  for (const b of sorted) {
    const same = out.findLast((o) => o.kind === b.kind && o.reason === b.reason);
    if (same && b.from <= same.to) same.to = Math.max(same.to, b.to);
    else out.push({ ...b });
  }
  return out.sort((a, b) => a.from - b.from);
}

/** A live trade as the seam check reads it (the chart's buffered trades). */
export type LiveTrade = { price: number; size: number; aggr: number; tsMs: number };

/**
 * Live-vs-history seam check (A1.5, §12.3: time bars built client-side must equal the history
 * plant's bars exactly). `live` are the trades the chart received since its previous history
 * cutoff, in arrival order; `history` the display bars about to replace them, complete for
 * everything before `cutoffTs`. Rebuilds the live bars with the chart's own `BarBuilder` rules and
 * compares every bar that live trades covered **completely**: never the first one (the stream may
 * have started inside it), never one still forming at the cutoff, never one overlapping `exclude`
 * (md outages, dropped trades). Open, high, low, close and volume must match exactly; a live bar
 * missing from the history counts too. Returns the mismatching bars' `[open, end)` ranges.
 * Time bars only: trade-built bars are rebuilt from raw trades anyway, and a live session bar is
 * never complete.
 */
export function seamMismatches(
  spec: BarSpec,
  live: Iterable<LiveTrade>,
  history: readonly BarRecord[],
  cutoffTs: number,
  exclude: readonly HistRange[] = [],
): HistRange[] {
  if (spec.kind !== "time") return [];
  const b = new BarBuilder(spec);
  for (const t of live) if (t.tsMs < cutoffTs) b.push(t.price, t.size, t.aggr, t.tsMs);
  const n = b.series.length;
  if (n < 3) return [];
  const byOpen = new Map<number, BarRecord>();
  for (const h of history) byOpen.set(h.openTs, h);
  const out: HistRange[] = [];
  for (let i = 1; i < n - 1; i++) {
    const lb = b.series.bar(i);
    const end = Math.min(lb.openTs + spec.periodMs, b.series.bar(i + 1).openTs);
    if (end > cutoffTs) break;
    if (exclude.some((x) => x.from < end && x.to > lb.openTs)) continue;
    const hb = byOpen.get(lb.openTs);
    const same =
      hb !== undefined &&
      hb.open === lb.open &&
      hb.high === lb.high &&
      hb.low === lb.low &&
      hb.close === lb.close &&
      hb.volume === lb.volume;
    if (!same) out.push({ from: lb.openTs, to: end });
  }
  return coalesce(out);
}

/** Group bars (time order) by UTC day. */
export function splitByDay(bars: readonly BarRecord[]): Map<number, BarRecord[]> {
  const out = new Map<number, BarRecord[]>();
  for (const b of bars) {
    const d = dayStart(b.openTs);
    let list = out.get(d);
    if (!list) {
      list = [];
      out.set(d, list);
    }
    list.push(b);
  }
  return out;
}

const FIELDS = 9;

/** Pack bars into one float64 buffer (9 integer fields per bar) for IndexedDB. */
export function packBars(bars: readonly BarRecord[]): ArrayBuffer {
  const a = new Float64Array(bars.length * FIELDS);
  let k = 0;
  for (const b of bars) {
    a[k++] = b.openTs;
    a[k++] = b.open;
    a[k++] = b.high;
    a[k++] = b.low;
    a[k++] = b.close;
    a[k++] = b.volume;
    a[k++] = b.buyVolume;
    a[k++] = b.sellVolume;
    a[k++] = b.trades;
  }
  return a.buffer;
}

/** Inverse of `packBars`; null when the buffer is not a whole number of bars. */
export function unpackBars(buf: ArrayBuffer): BarRecord[] | null {
  if (buf.byteLength % (FIELDS * 8) !== 0) return null;
  const a = new Float64Array(buf);
  const out: BarRecord[] = [];
  for (let k = 0; k < a.length; k += FIELDS) {
    out.push({
      openTs: a[k] ?? 0,
      open: a[k + 1] ?? 0,
      high: a[k + 2] ?? 0,
      low: a[k + 3] ?? 0,
      close: a[k + 4] ?? 0,
      volume: a[k + 5] ?? 0,
      buyVolume: a[k + 6] ?? 0,
      sellVolume: a[k + 7] ?? 0,
      trades: a[k + 8] ?? 0,
    });
  }
  return out;
}

/** FNV-1a 32-bit over the bytes (content checksum for cached blobs, A1.5). */
export function fnv1a32(buf: ArrayBuffer): number {
  const bytes = new Uint8Array(buf);
  let h = 0x811c9dc5;
  for (const byte of bytes) {
    h ^= byte;
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/**
 * LRU eviction plan: evict least-recently-viewed entries until the total fits `capBytes`, never
 * touching `protect` (what is being written or shown right now). Returns the keys to delete.
 */
export function planEviction(
  entries: readonly { key: string; bytes: number; lastViewed: number }[],
  capBytes: number,
  protect: ReadonlySet<string> = new Set(),
): string[] {
  let total = 0;
  for (const e of entries) total += e.bytes;
  if (total <= capBytes) return [];
  const victims = entries
    .filter((e) => !protect.has(e.key))
    .sort((a, b) => a.lastViewed - b.lastViewed || a.key.localeCompare(b.key));
  const out: string[] = [];
  for (const v of victims) {
    if (total <= capBytes) break;
    out.push(v.key);
    total -= v.bytes;
  }
  return out;
}
